import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Plus, Trash2, Pencil, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { title: "", description: "", embed: "", thumbnail: "", sort_order: 0, published: true };

export function VideoLibraryAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/library/videos").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setF({ title: v.title, description: v.description || "", embed: "", thumbnail: v.thumbnail || "", sort_order: v.sort_order || 0, published: v.published });
    setOpen(true);
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    if (!editing && !f.embed.trim()) { toast.error("Paste the Vadoo embed"); return; }
    setSaving(true);
    const payload = {
      title: f.title, description: f.description, thumbnail: f.thumbnail,
      sort_order: parseInt(f.sort_order) || 0, published: f.published,
    };
    if (f.embed.trim()) payload.embed = f.embed;
    try {
      if (editing) await api.patch(`/admin/library/videos/${editing.video_id}`, payload);
      else await api.post("/admin/library/videos", payload);
      setOpen(false); setF(empty); setEditing(null); load();
      toast.success(editing ? "Video updated" : "Video added to the library");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (v) => {
    await api.patch(`/admin/library/videos/${v.video_id}`, { published: !v.published }).catch(() => {});
    load();
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}" from the video library?`)) return;
    await api.delete(`/admin/library/videos/${v.video_id}`).catch(() => {});
    load();
    toast.success("Video removed");
  };

  return (
    <div className="mb-10" data-testid="admin-video-library">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Film className="w-4 h-4 text-gold" /><h2 className="label-mono">Members Video Library (Vadoo)</h2></div>
        <Button onClick={openNew} size="sm" className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="add-library-video-btn"><Plus className="w-4 h-4 mr-1" /> Add Video</Button>
      </div>
      <p className="text-xs text-white/50 mb-3">Paste a single Vadoo video embed (iframe code or the src URL). Members with active access see published videos — no code changes needed. Channel embeds are rejected.</p>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-white/40 card-surface rounded-md p-4">No videos yet. Click “Add Video” to publish your first one.</div>}
        {items.map((v) => (
          <div key={v.video_id} className="card-surface rounded-md p-4 flex items-center gap-3" data-testid={`library-admin-row-${v.video_id}`}>
            <div className="w-14 h-9 rounded bg-black/50 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
              {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" /> : <Film className="w-4 h-4 text-white/30" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white font-medium truncate">{v.title}</div>
              <div className="text-[11px] font-mono text-white/40">order {v.sort_order} · {v.published ? "published" : "hidden"}</div>
            </div>
            <button onClick={() => togglePublish(v)} title={v.published ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${v.video_id}`}
              className="p-2 text-white/50 hover:text-gold transition-colors">{v.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
            <button onClick={() => openEdit(v)} title="Edit" data-testid={`edit-library-${v.video_id}`}
              className="p-2 text-white/50 hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(v)} title="Remove" data-testid={`delete-library-${v.video_id}`}
              className="p-2 text-white/50 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setF(empty); } }}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Video" : "Add Video"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-mono block mb-1.5">Title</label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="library-title-input" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Description (optional)</label>
              <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-charcoal border-white/15" data-testid="library-desc-input" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Vadoo embed {editing && <span className="text-white/40 normal-case">(leave blank to keep current)</span>}</label>
              <Textarea rows={3} value={f.embed} onChange={(e) => setF({ ...f, embed: e.target.value })} placeholder='Paste <iframe …src="https://…vadoo.tv/…"></iframe> or the src URL' className="bg-charcoal border-white/15 font-mono text-xs" data-testid="library-embed-input" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Thumbnail URL (optional)</label>
              <Input value={f.thumbnail} onChange={(e) => setF({ ...f, thumbnail: e.target.value })} placeholder="https://…/thumb.jpg" className="bg-charcoal border-white/15" data-testid="library-thumb-input" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="label-mono block mb-1.5">Sort order</label>
                <Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} className="bg-charcoal border-white/15" data-testid="library-sort-input" />
              </div>
              <label className="flex items-center gap-2 text-sm text-white/80 mt-6 cursor-pointer">
                <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="accent-gold w-4 h-4" data-testid="library-published-checkbox" />
                Published
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-library-video-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save Changes" : "Add Video")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
